import {
  DollarSign,
  Plus,
  Search,
  Settings,
  TrendingUp,
  Users,
} from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/app/common/components/button/Button.tsx'
import { Input } from '@/app/common/components/input/Input.tsx'
import { currencyFormatter } from '@/lib/utils.ts'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { Badge } from '@/shad/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select'
import { format, parseISO } from 'date-fns'
import { Link } from 'react-router-dom'
import { z } from 'zod'
import {
  Card,
  CardContent,
  CardHeader,
} from '../../../../common/components/Card.tsx'
import CreatePurse from '../create-purse/create-purse.tsx'
import { ProgressBar } from '../progress-bar.tsx'
import {
  FundingSourceSchema,
  Purse,
  PurseDetail,
  PurseSchema,
} from '../../schema.ts'
import { calculateFundsDistributed } from '../../utils.ts'
import { useSelector } from 'react-redux'
import { selectSelectedProgram } from '@/storage/programs/slice.ts'

interface PursesManagementProps {
  purses: z.infer<typeof PurseSchema>[]
  purseSource: z.infer<typeof FundingSourceSchema>[]
}

export function PursesManagement({
  purses,
  purseSource,
}: PursesManagementProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [fundingFilter, setFundingFilter] = useState('all')
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  const programId = useSelector(selectSelectedProgram).id

  const filteredPurses: z.infer<typeof PurseSchema>[] = purses.filter(
    (purse) => {
      const matchesSearch = purse.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
      const matchesSource =
        sourceFilter === 'all' || purse.source_type === Number(sourceFilter)
      const matchesFunding =
        fundingFilter === 'all' ||
        purse.sub_source_type === Number(fundingFilter)
      return matchesSearch && matchesSource && matchesFunding
    }
  )

  const getFundingSourceName = (sourceTypeId: number): string => {
    const source = purseSource.find((fs) => fs.id === sourceTypeId)
    return source ? source.name : 'Unknown Source'
  }

  const getSubTypeName = (purse: Purse): string => {
    const source = purseSource.find((source) => {
      return purse.source_type === source.id
    })
    if (!source) {
      return 'Unknown Sub Type'
    }

    const subType = source.sub_types.find((subType) => {
      return purse.sub_source_type === subType.id
    })?.name

    if (!subType) {
      return 'Unknown Sub Type'
    }

    return subType
  }

  // Calculate summary totals
  const totalFunds = purses.reduce(
    (sum: number, purse: PurseDetail) => sum + purse.total,
    0
  )
  const totalDistributed = purses.reduce(
    (sum: number, purse: PurseDetail) => sum + purse.balance_distribuited,
    0
  )
  const totalRemaining = purses.reduce(
    (sum: number, purse: PurseDetail) => sum + purse.balance_available,
    0
  )

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
            <div className="text-sm font-medium text-gray-800">Total Funds</div>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="">
            <div className="text-2xl font-semibold text-gray-800 font-sans">
              {currencyFormatter.format(totalFunds)}
            </div>
            <p className="text-sm text-gray-600 text-muted-foreground">
              Across {purses.length} purses
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
            <div className="text-sm font-medium">Distributed</div>{' '}
            <div className="text-sm font-medium">Distributed</div>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {currencyFormatter.format(totalDistributed)}
            </div>
            <p className="text-sm text-muted-foreground text-gray-600">
              {calculateFundsDistributed({
                totalDistributed,
                totalAvailable: totalFunds,
              })}
              % of total funds
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
            <div className="text-sm font-medium">Remaining</div>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {currencyFormatter.format(totalRemaining)}
            </div>
            <p className="text-sm text-gray-600">Available for distribution</p>
          </CardContent>
        </Card>
      </div>

      {/*List Header*/}
      <div className="pt-4 flex justify-between items-end">
        <h2 className="text-xl">Purses ({filteredPurses.length})</h2>
        <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Create Purse
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search purses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-full shadow-none "
          />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Source Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {purseSource.map((source) => (
              <SelectItem key={source.id} value={String(source.id)}>
                {source.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fundingFilter} onValueChange={setFundingFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Funding Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Funding</SelectItem>
            {purseSource
              .flatMap((source) => source.sub_types)
              .map((subType) => (
                <SelectItem key={subType.id} value={String(subType.id)}>
                  {subType.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Purses Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredPurses.map((purse) => (
          <Card key={purse.id} className="hover:shadow-md transition-shadow ">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="text-lg font-semibold">{purse.name}</div>

                  <div className="mt-1 text-xs text-gray-500 font-mono truncate">
                    PROGRAM ID: {programId} - PURSE ID: {String(purse.id)}
                  </div>

                  <div className="mt-2 flex gap-2">
                    <Badge variant="outline">
                      {getFundingSourceName(purse.source_type)}
                    </Badge>
                    <Badge variant="outline">{getSubTypeName(purse)}</Badge>
                  </div>
                  {purse.awarding_method_name && (
                    <Badge className="mt-2 text-black">
                      Award By: {purse.awarding_method_name}
                    </Badge>
                  )}
                </div>

                <Link
                  to={AppRoutePaths.purse_settings.id.replace(
                    ':purseId',
                    String(purse.id)
                  )}
                  className="hover:bg-gray-100 transition-colors h-8 w-8 inline-flex items-center justify-center rounded-md"
                >
                  <Settings className="size-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Funds</span>
                  <span className="font-medium">
                    {currencyFormatter.format(purse.total)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Distributed</span>
                  <span className="font-medium text-red-600">
                    {currencyFormatter.format(purse.balance_distribuited)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Remaining</span>
                  <span className="font-medium text-green-600">
                    {currencyFormatter.format(purse.balance_available)}
                  </span>
                </div>
              </div>
              <ProgressBar
                totalDistributed={purse.balance_distribuited}
                totalRemaining={purse.balance_available}
              />
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>
                  Created: {format(parseISO(purse.created_at), 'yyyy-MM-dd')}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPurses.length === 0 && (
        <div className="text-center py-12">
          {purses.length === 0 ? (
            <div className="">
              <p className="text-gray-800 font-medium">
                No purses have been created yet.
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Get started by creating your first purse.
              </p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create Purse
              </Button>
            </div>
          ) : (
            <p className="text-gray-500">
              No purses found matching your criteria.
            </p>
          )}
        </div>
      )}

      <CreatePurse
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        fundingSources={purseSource}
      />
    </div>
  )
}
